import React, { useEffect, useMemo, useState } from "react";
import { Link, useLocation, useNavigate, useParams } from "react-router-dom";
import DOMPurify from "dompurify";
import { useLanguage } from "../contexts/LanguageContext";
import { useAuth } from "../contexts/AuthContext";
import logger from "../utils/logger.js";
import {
  applyToJob,
  formatJobDate,
  getJobDescription,
  getJobSalaryLabel,
  getJobSummary,
  getJobTitle,
  getMyJobApplications,
  getJobDetails,
} from "../api/jobs";

function getStatusMeta(status, isAr) {
  const map = {
    pending: { className: "text-bg-warning", label: isAr ? "قيد المراجعة" : "Pending" },
    reviewed: { className: "text-bg-info", label: isAr ? "تمت المراجعة" : "Reviewed" },
    shortlisted: { className: "text-bg-primary", label: isAr ? "في القائمة المختصرة" : "Shortlisted" },
    rejected: { className: "text-bg-danger", label: isAr ? "مرفوض" : "Rejected" },
    accepted: { className: "text-bg-success", label: isAr ? "مقبول" : "Accepted" },
  };

  return map[String(status || "").toLowerCase()] || {
    className: "text-bg-secondary",
    label: status || (isAr ? "غير معروف" : "Unknown"),
  };
}

export default function JobDetails() {
  const { jobRef } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const { lang } = useLanguage();
  const { isLogged, user } = useAuth();
  const isAr = lang === "ar";

  const [job, setJob] = useState(null);
  const [application, setApplication] = useState(null);
  const [loading, setLoading] = useState(true);
  const [actionLoading, setActionLoading] = useState(false);
  const [error, setError] = useState("");
  const [notice, setNotice] = useState("");
  const [form, setForm] = useState({
    full_name: "",
    email: "",
    phone: "",
    cv_url: "",
    portfolio_url: "",
    linkedin_url: "",
    cover_letter: "",
  });

  useEffect(() => {
    let active = true;

    async function load() {
      try {
        setLoading(true);
        setError("");
        setNotice("");

        const details = await getJobDetails(jobRef);
        if (!active) return;
        setJob(details);

        if (isLogged) {
          try {
            const applications = await getMyJobApplications();
            if (!active) return;
            const matched = applications.find(
              (item) => Number(item?.job?.id) === Number(details?.id)
            );
            setApplication(matched || null);
          } catch (applicationsError) {
            logger.warn("My job applications fetch failed", applicationsError);
          }
        } else {
          setApplication(null);
        }
      } catch (err) {
        logger.error("Job details error", err);
        if (!active) return;
        setError(isAr ? "تعذر تحميل تفاصيل الوظيفة." : "Failed to load job details.");
      } finally {
        if (active) {
          setLoading(false);
        }
      }
    }

    load();

    return () => {
      active = false;
    };
  }, [jobRef, isAr, isLogged]);

  useEffect(() => {
    setForm((current) => ({
      ...current,
      full_name: current.full_name || user?.name || "",
      email: current.email || user?.email || "",
      phone: current.phone || user?.phone || "",
    }));
  }, [user]);

  const title = useMemo(() => getJobTitle(job, isAr), [job, isAr]);
  const summary = useMemo(() => getJobSummary(job, isAr), [job, isAr]);
  const description = useMemo(() => getJobDescription(job, isAr), [job, isAr]);
  const statusMeta = getStatusMeta(application?.status, isAr);

  const handleRequireAuth = () => {
    navigate("/login", { state: { from: location } });
  };

  const handleChange = (field) => (event) => {
    setForm((current) => ({
      ...current,
      [field]: event.target.value,
    }));
  };

  const handleApply = async () => {
    if (!job) return;
    if (!isLogged) {
      handleRequireAuth();
      return;
    }

    try {
      setActionLoading(true);
      setError("");
      setNotice("");

      const response = await applyToJob(job.id, {
        full_name: form.full_name.trim() || undefined,
        email: form.email.trim() || undefined,
        phone: form.phone.trim() || undefined,
        cv_url: form.cv_url.trim() || undefined,
        portfolio_url: form.portfolio_url.trim() || undefined,
        linkedin_url: form.linkedin_url.trim() || undefined,
        cover_letter: form.cover_letter.trim() || undefined,
      });

      const nextApplication = response?.data?.application || null;
      setApplication(nextApplication);
      setNotice(
        response?.message ||
          (isAr ? "تم إرسال طلب التقديم بنجاح." : "Job application submitted successfully.")
      );
    } catch (err) {
      logger.error("Job apply error", err);
      setError(
        err?.message || (isAr ? "تعذر إرسال طلب التقديم." : "Unable to submit your application.")
      );
    } finally {
      setActionLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="container py-5 text-center">
        <div className="spinner-border text-primary mb-3" role="status" />
        <p className="mb-0">{isAr ? "جارِ تحميل الوظيفة..." : "Loading job..."}</p>
      </div>
    );
  }

  if (error && !job) {
    return (
      <div className="container py-5">
        <div className="alert alert-danger rounded-4">{error}</div>
      </div>
    );
  }

  return (
    <div className="container py-4 py-lg-5" dir={isAr ? "rtl" : "ltr"}>
      <div className="row g-4 g-lg-5">
        <div className="col-lg-7">
          <div className="course-details__hero rounded-4 overflow-hidden shadow-sm mb-4">
            <img
              src={
                job?.cover_image ||
                "https://images.unsplash.com/photo-1521737604893-d14cc237f11d?auto=format&fit=crop&w=1400&q=80"
              }
              alt={title}
              className="course-details__image"
            />
          </div>

          <div className="d-flex flex-wrap gap-2 mb-3">
            <span className="badge text-bg-light">{job?.company || "Mousa Group"}</span>
            <span className="badge text-bg-light">
              {job?.employment_type || (isAr ? "دوام كامل" : "full-time")}
            </span>
            {job?.location ? <span className="badge text-bg-light">{job.location}</span> : null}
            {job?.experience_level ? (
              <span className="badge text-bg-light">{job.experience_level}</span>
            ) : null}
          </div>

          <h1 className="display-6 fw-bold mb-3">{title}</h1>
          <p className="lead text-muted mb-4">
            {summary || (isAr ? "وصف الوظيفة سيظهر هنا." : "Job summary will appear here.")}
          </p>

          <div className="card border-0 shadow-sm rounded-4 mb-4">
            <div className="card-body p-4">
              <h2 className="h4 fw-bold mb-3">{isAr ? "عن الوظيفة" : "About this role"}</h2>
              <div
                className="text-muted course-rich-text"
                dangerouslySetInnerHTML={{
                  __html: DOMPurify.sanitize(
                    description ||
                    (isAr ? "<p>لا يوجد وصف مفصل بعد.</p>" : "<p>No detailed description yet.</p>")
                  ),
                }}
              />
            </div>
          </div>

          <div className="card border-0 shadow-sm rounded-4">
            <div className="card-body p-4">
              <h2 className="h4 fw-bold mb-3">
                {isAr ? "لماذا هذه الوظيفة؟" : "Why this opportunity?"}
              </h2>
              <div className="consultation-steps">
                <div>
                  <strong>{isAr ? "مباشرة وواضحة" : "Direct and clear"}</strong>
                  <p className="text-muted mb-0">
                    {isAr
                      ? "تفاصيل الدور، نوع التوظيف، والموقع متاحة من نفس الصفحة."
                      : "Role details, employment type, and location are all visible in one place."}
                  </p>
                </div>
                <div>
                  <strong>{isAr ? "تقديم سريع" : "Fast application"}</strong>
                  <p className="text-muted mb-0">
                    {isAr
                      ? "قدّم من حسابك الحالي، وأضف روابط السيرة أو الأعمال بسهولة."
                      : "Apply from your current account and attach CV or portfolio links easily."}
                  </p>
                </div>
                <div>
                  <strong>{isAr ? "متابعة لاحقًا" : "Track it later"}</strong>
                  <p className="text-muted mb-0">
                    {isAr
                      ? "تقدر تراجع حالة طلبك من صفحة وظائفي المتقدم لها."
                      : "You can review your application status later from My Job Applications."}
                  </p>
                </div>
              </div>
            </div>
          </div>
        </div>

        <div className="col-lg-5">
          <div className="card border-0 shadow-sm rounded-4 course-purchase-card">
            <div className="card-body p-4 p-lg-5">
              <div className="consultation-price-box mb-4">
                <div className="text-muted small mb-2">
                  {isAr ? "الراتب" : "Salary"}
                </div>
                <div className="h3 fw-bold mb-0">{getJobSalaryLabel(job, isAr)}</div>
              </div>

              <div className="course-meta-list mb-4">
                <div>
                  <span>{isAr ? "الشركة" : "Company"}</span>
                  <strong>{job?.company || "Mousa Group"}</strong>
                </div>
                <div>
                  <span>{isAr ? "نوع التوظيف" : "Employment"}</span>
                  <strong>{job?.employment_type || (isAr ? "غير محدد" : "Not specified")}</strong>
                </div>
                <div>
                  <span>{isAr ? "الموقع" : "Location"}</span>
                  <strong>{job?.location || (isAr ? "مرن" : "Flexible")}</strong>
                </div>
                <div>
                  <span>{isAr ? "المستوى" : "Level"}</span>
                  <strong>{job?.experience_level || (isAr ? "عام" : "General")}</strong>
                </div>
                <div>
                  <span>{isAr ? "آخر موعد" : "Deadline"}</span>
                  <strong>
                    {job?.application_deadline
                      ? formatJobDate(job.application_deadline, lang)
                      : isAr
                        ? "مفتوح"
                        : "Open"}
                  </strong>
                </div>
              </div>

              {application ? (
                <div className="consultation-booking-summary alert alert-success rounded-4">
                  <div className="d-flex flex-wrap align-items-center justify-content-between gap-2 mb-2">
                    <strong>{isAr ? "تم إرسال طلبك" : "Your application was submitted"}</strong>
                    <span className={`badge ${statusMeta.className}`}>{statusMeta.label}</span>
                  </div>
                  <div className="small text-muted mb-2">
                    {isAr ? "رقم الطلب:" : "Application #:"} {application.id}
                  </div>
                  <div className="d-flex flex-wrap gap-2 mt-3">
                    <Link to="/my-job-applications" className="btn btn-primary rounded-pill px-4">
                      {isAr ? "وظائفي المتقدم لها" : "My Job Applications"}
                    </Link>
                  </div>
                </div>
              ) : (
                <>
                  <div className="mb-3">
                    <label className="form-label fw-semibold">
                      {isAr ? "الاسم الكامل" : "Full name"}
                    </label>
                    <input
                      type="text"
                      className="form-control"
                      value={form.full_name}
                      onChange={handleChange("full_name")}
                    />
                  </div>

                  <div className="mb-3">
                    <label className="form-label fw-semibold">
                      {isAr ? "البريد الإلكتروني" : "Email"}
                    </label>
                    <input
                      type="email"
                      className="form-control"
                      value={form.email}
                      onChange={handleChange("email")}
                    />
                  </div>

                  <div className="mb-3">
                    <label className="form-label fw-semibold">
                      {isAr ? "رقم الهاتف" : "Phone"}
                    </label>
                    <input
                      type="text"
                      className="form-control"
                      value={form.phone}
                      onChange={handleChange("phone")}
                    />
                  </div>

                  <div className="mb-3">
                    <label className="form-label fw-semibold">
                      {isAr ? "رابط السيرة الذاتية" : "CV URL"}
                    </label>
                    <input
                      type="url"
                      className="form-control"
                      value={form.cv_url}
                      onChange={handleChange("cv_url")}
                      placeholder="https://..."
                    />
                  </div>

                  <div className="mb-3">
                    <label className="form-label fw-semibold">
                      {isAr ? "رابط الأعمال" : "Portfolio URL"}
                    </label>
                    <input
                      type="url"
                      className="form-control"
                      value={form.portfolio_url}
                      onChange={handleChange("portfolio_url")}
                      placeholder="https://..."
                    />
                  </div>

                  <div className="mb-3">
                    <label className="form-label fw-semibold">
                      {isAr ? "رابط لينكدإن" : "LinkedIn URL"}
                    </label>
                    <input
                      type="url"
                      className="form-control"
                      value={form.linkedin_url}
                      onChange={handleChange("linkedin_url")}
                      placeholder="https://linkedin.com/in/..."
                    />
                  </div>

                  <div className="mb-4">
                    <label className="form-label fw-semibold">
                      {isAr ? "رسالة التقديم" : "Cover letter"}
                    </label>
                    <textarea
                      rows="5"
                      className="form-control"
                      value={form.cover_letter}
                      onChange={handleChange("cover_letter")}
                      placeholder={
                        isAr
                          ? "اكتب باختصار لماذا تناسب هذه الوظيفة."
                          : "Briefly explain why you are a good fit for this role."
                      }
                    />
                  </div>

                  <div className="d-grid gap-2">
                    <button
                      type="button"
                      className="btn btn-primary btn-lg rounded-pill"
                      onClick={handleApply}
                      disabled={actionLoading}
                    >
                      {actionLoading
                        ? isAr
                          ? "جارِ الإرسال..."
                          : "Submitting..."
                        : isAr
                          ? "قدّم الآن"
                          : "Apply Now"}
                    </button>
                    <Link to="/my-job-applications" className="btn btn-outline-dark rounded-pill">
                      {isAr ? "عرض طلباتي" : "View My Applications"}
                    </Link>
                  </div>
                </>
              )}

              {!isLogged ? (
                <div className="alert alert-warning rounded-4 mt-4 mb-0">
                  {isAr
                    ? "سجّل الدخول أولًا حتى تتمكن من التقديم."
                    : "Please sign in first so you can apply."}
                </div>
              ) : null}

              {notice ? <div className="alert alert-success rounded-4 mt-4 mb-0">{notice}</div> : null}
              {error ? <div className="alert alert-danger rounded-4 mt-4 mb-0">{error}</div> : null}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
